import {createStyles} from '@mantine/core';
import React from 'react';

import {
  REDESIGN_DRAWER_VERTICAL_PADDING_PX,
  REDESIGN_HEADER_ICON_CHIP_SIZE_PX,
} from '../../ui/AIStyleConstants';
import {MINIMIZED_HEADER_HEIGHT} from './RedesignMinimizedHeader';
import RedesignSidebarActions from './RedesignSidebarActions';

/**
 * How far the drawer's top padding drops this toolbar below the band the header's controls center on
 * (see MINIMIZED_HEADER_HEIGHT), and therefore how far back up the chips have to come to line up with
 * the hamburger, share, and expand/collapse glyphs.
 */
const BAND_OFFSET_PX =
  REDESIGN_DRAWER_VERTICAL_PADDING_PX - (MINIMIZED_HEADER_HEIGHT - REDESIGN_HEADER_ICON_CHIP_SIZE_PX) / 2;

const useStyles = createStyles(() => ({
  toolbar: {
    // Tracks the action chips' own height so they aren't clipped by the toolbar.
    height: REDESIGN_HEADER_ICON_CHIP_SIZE_PX,
    // Onto the header's band, then hand the space back so the divider and nav below don't move up too.
    marginTop: -BAND_OFFSET_PX,
    marginBottom: BAND_OFFSET_PX,
    flex: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
  },
}));

export interface RedesignSidebarToolbarProps {
  /**
   * Whether the drawer is open. The chips are dropped as soon as it starts closing: they are
   * right-aligned in the drawer's content, so they would otherwise slide left as the drawer's
   * padding collapses (and snap in from a dragged width, which is released on close) while the
   * closing animation is still running. The empty toolbar keeps reserving its height so the divider
   * and nav below it don't move.
   */
  isDrawerOpen: boolean;
}

/**
 * Top-right controls of the redesigned sidebar (wide view). The buttons themselves live in the shared
 * {@link RedesignSidebarActions} so the wide sidebar and the semi-expanded header stay in sync. The
 * sidebar toggle (hamburger) is a separate floating control rendered by the sidepanel.
 */
export default function RedesignSidebarToolbar({isDrawerOpen}: RedesignSidebarToolbarProps): JSX.Element {
  const {classes} = useStyles();
  return <div className={classes.toolbar}>{isDrawerOpen && <RedesignSidebarActions />}</div>;
}
